import React, {
    useCallback,
    useState,
} from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { parsePaging, parseSorting } from '../../utils/queryString';
import Constants from '../../utils/Constants';
import useExtendedQuery from '../../hooks/useExtendedQuery';
import {
    ALL_AUCTIONEER_DATA_TEMPLATES,
    ON_AUCTIONEER_DATA_TEMPLATE_CHANGE,
} from '../../graphql/auctioneerDataTemplates';
import Spinner from '../Spinner';
import Paginator from '../Paginator';
import AuctioneerDataTemplatesList from './AuctioneerDataTemplatesList';
import Error from '../errors/Error';
import CreateAuctioneerDataTemplateRow from './CreateAuctioneerDataTemplateRow';
import useExtendedSubscription from '../../hooks/useExtendedSubscription';
import { showInfoNotification } from '../../actions/notifications';
import SubscriptionActions from '../../utils/SubscriptionActions';
import UploadButton from '../buttons/UploadButton';
import AuctioneerDataTemplateUploadCreate from './AuctioneerDataTemplateUploadCreate';
import DownloadSampleFile from '../DownloadSampleFile';
import { hasPrivilege } from '../../hocs/withPrivileges';
import Privileges from '../../utils/Privileges';
import useAuthentication from '../../hooks/useAuthentication';

export function createNotificationText(t, action, name) {
    let notification;
    switch (action) {
        case SubscriptionActions.AUCTIONEER_DATA_TEMPLATE_CREATED:
            notification = t('auctioneer-data-templates.notification.auctioneer-data-template-create-thirdparty', { name });
            break;
        case SubscriptionActions.AUCTIONEER_DATA_TEMPLATE_UPDATED:
            notification = t('auctioneer-data-templates.notification.auctioneer-data-template-update-thirdparty', { name });
            break;
        case SubscriptionActions.AUCTIONEER_DATA_TEMPLATE_DELETED:
            notification = t('auctioneer-data-templates.notification.auctioneer-data-template-delete-thirdparty', { name });
            break;
        default:
            notification = t('auctioneer-data-templates.notification.auctioneer-data-templates-modified-thirdparty');
    }
    return notification;
}

function AuctioneerDataTemplates() {
    const location = useLocation();
    const { t } = useTranslation();
    const { userPrivileges } = useAuthentication();
    const { page, perPage } = parsePaging(location.search);
    const {
        sortKey = 'name',
        sortDirection = Constants.SORT_ASC,
    } = parseSorting(location.search);
    const dispatch = useDispatch();
    const [refetchProps, setRefetchProps] = useState({
        perPage,
        page,
        sortKey,
        sortDirection,
    });
    const {
        loading,
        error,
        data,
        refetch,
    } = useExtendedQuery(ALL_AUCTIONEER_DATA_TEMPLATES, {
        variables: refetchProps,
    });
    const onPageChange = (newPage) => setRefetchProps({
        ...refetchProps,
        page: newPage,
    });
    const onSort = (newSorting) => setRefetchProps({
        ...refetchProps,
        sortKey: newSorting.key,
        sortDirection: newSorting.direction,
    });
    const onUpload = useCallback(() => refetch(refetchProps), [refetch, refetchProps]);
    useExtendedSubscription(ON_AUCTIONEER_DATA_TEMPLATE_CHANGE, {
        refetch,
        refetchVariables: refetchProps,
        dataProperty: 'auctioneerDataTemplateChanged',
        onChangeCallback: ({ action, model: { name } }) => {
            if (hasPrivilege(userPrivileges, [Privileges.AUCTIONEER_DATA_TEMPLATE_EDIT])) {
                const notification = createNotificationText(t, action, name);
                dispatch(showInfoNotification(notification));
            }
        },
    });

    const extraVariablesComponent = (setExtraVariables, setExtraVariablesValid) => (
        <AuctioneerDataTemplateUploadCreate
            setExtraVariables={setExtraVariables}
            setExtraVariablesValid={setExtraVariablesValid}
        />
    );

    let result;
    let auctioneerDataTemplateProps = null;
    if (loading) {
        result = <Spinner className="auctioneer-data-templates-loading" />;
    } else if (error) {
        result = <Error className="auctioneer-data-templates-error" error={error} />;
    } else {
        auctioneerDataTemplateProps = {
            availableAuctioneers: data.allAuctioneerDataTemplates.availableAuctioneers,
            availableSubmarkets: data.allAuctioneerDataTemplates.availableSubmarkets,
            maxQuantitySum: data.allAuctioneerDataTemplates.maxQuantitySum,
        };
        result = (
            <Paginator
                totalCount={data.allAuctioneerDataTemplates.auctioneerDataTemplateList.totalCount}
                perPage={perPage}
                currentPage={refetchProps.page}
                onPageChange={onPageChange}
                bottom
            >
                <AuctioneerDataTemplatesList
                    auctioneerDataTemplates={data.allAuctioneerDataTemplates.auctioneerDataTemplateList.models}
                    auctioneerDataTemplateProps={auctioneerDataTemplateProps}
                    onSort={onSort}
                    sortingParam={{ key: refetchProps.sortKey, direction: refetchProps.sortDirection }}
                    onUpload={onUpload}
                />
            </Paginator>
        );
    }
    return (
        <>
            <h4><FontAwesomeIcon icon="file-alt" />&nbsp;{t('title.auctioneer-data-templates')}</h4>
            {auctioneerDataTemplateProps && <CreateAuctioneerDataTemplateRow auctioneerDataTemplateProps={auctioneerDataTemplateProps} />}
            <div className="search-and-filter">
                <UploadButton
                    label={t('auctioneer-data-templates.upload-auctioneer-data-template')}
                    className="button-bordered"
                    uploadPath={Constants.UPLOAD_AUCTIONEER_DATA_TEMPLATE_PATH}
                    accept=".xls,.xlsx"
                    extraVariablesComp={extraVariablesComponent}
                    onUpload={onUpload}
                >
                    <DownloadSampleFile
                        text={t('auctioneer-data-templates.download-sample-file-text')}
                        fileName="sampleAuctioneerMatrixUploadFile"
                        sampleType="AUCTIONEER_MATRIX"
                        fileTypes={['XLSX']}
                    />
                </UploadButton>
            </div>
            {result}
        </>
    );
}

export default AuctioneerDataTemplates;
